import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import * as productService from '../lib/services/productService';
import { useCart } from '../context/CartContext';
import './ProductDetail.css';


const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart, cartItems } = useCart();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);


  const fetchProduct = useCallback(async () => {
    try {
      const product = await productService.getProductById(id);
      setProduct(product);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching product:', error);
      setLoading(false);
    }
  }, [id]);


  useEffect(() => {
    fetchProduct();
  }, [fetchProduct]);


  const getProductQuantityInCart = (productId) => {
    const cartItem = cartItems.find((item) => item.id === productId);
    return cartItem ? cartItem.quantity : 0;
  };


  const isProductNew = (createdAt) => {
    if (!createdAt) return false;
    const productDate = new Date(createdAt);
    const currentDate = new Date();
    const twoMonthsAgo = new Date(currentDate.setMonth(currentDate.getMonth() - 1));
    return productDate > twoMonthsAgo;
  };


  const handleAddToCart = () => {
    if (product) {
      addToCart(product, quantity);
      setQuantity(1);
    }
  };


  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }


  if (!product) {
    return (
      <div className="container">
        <div className="no-product">
          <h2>Product not found</h2>
          <button onClick={() => navigate('/products')} className="btn btn-primary">
            Back to Products
          </button>
        </div>
      </div>
    );
  }


  return (
    <div className="product-detail-page">
      <section className="section">
        <div className="container">
          <button className="btn btn-outline" style={{ marginBottom: '20px' }} onClick={() => navigate('/products')}>
            ← Back to Products
          </button>
          <div className="product-detail-content">
            <div className="product-detail-image">
              {product.image_url ? (
                <img src={product.image_url} alt={product.name} />
              ) : (
                <div className="image-placeholder" aria-label="No image available">No Image Available</div>
              )}
              {isProductNew(product.created_at) && (
                <span className="new-badge">⭐ New</span>
              )}
              {product.gluten_free && (
                <span className="gluten-free-tag">Gluten Free</span>
              )}
            </div>

            <div className="product-detail-info">
              <h1>{product.name}</h1>
              <p className="product-category">{product.category}</p>
              {product.subcategory && (
                <p className="product-subcategory">{product.subcategory}</p>
              )}
              {product.weight && product.weight > 0 && (
                <p className="product-weight" style={{ fontSize: '1em', color: '#666', margin: '10px 0', fontWeight: '500' }}>
                  📦 Weight: {product.weight}g
                </p>
              )}
              {product.discounted_price && product.discounted_price < product.price ? (
                <div className="price-with-discount">
                  <p className="product-price-large">
                    <span className="old-price">kr {Number(product.price).toFixed(2)} incl. VAT</span>
                    <span className="new-price large">kr {Number(product.discounted_price).toFixed(2)} incl. VAT</span>
                  </p>
                  <span className="discount-badge-large">
                    {product.discount_percent > 0
                      ? `${product.discount_percent}% OFF`
                      : `${Math.round(((product.price - product.discounted_price) / product.price) * 100)}% OFF`
                    }
                  </span>
                </div>
              ) : (
                <p className="product-price-large">kr {Number(product.price).toFixed(2)} incl. VAT</p>
              )}


              {product.description && (
                <div className="product-description-full">
                  <h3>Description</h3>
                  <p>{product.description}</p>
                </div>
              )}


              {product.ingredients && (
                <div className="product-ingredients">
                  <h3>Ingredients</h3>
                  <p>{product.ingredients}</p>
                </div>
              )}


              <div className="product-actions">
                <div className="quantity-selector">
                  <button
                    type="button"
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="quantity-btn"
                    disabled={product.stock_quantity === 0}
                  >
                    −
                  </button>
                  <span className="quantity-display">{quantity}</span>
                  <button
                    type="button"
                    onClick={() => setQuantity(quantity + 1)}
                    className="quantity-btn"
                    disabled={product.stock_quantity === 0}
                  >
                    +
                  </button>
                </div>
                <button 
                  onClick={handleAddToCart} 
                  className="btn btn-primary add-to-cart-btn"
                  disabled={product.stock_quantity === 0}
                >
                  🛒 {product.stock_quantity === 0 ? 'Out of Stock' : 'Add to Cart'}
                  {getProductQuantityInCart(product.id) > 0 && product.stock_quantity > 0 && (
                    <span className="cart-quantity-badge">{getProductQuantityInCart(product.id)}</span>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};


export default ProductDetail;





